'use client'

import { Button } from 'fukku/Button'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useEffect } from 'react'
import { useCartController } from 'shopping-cart/hooks/useCartController'
import { cx } from 'utils/cx'
import { useMatchMedia } from 'utils/matchMedia/hooks'

import { useColors } from '../../contexts/ColorsProvider/ColorsProvider'
import { useSizes } from '../../contexts/SizesProvider/SizesProvider'
import type { CommonProps } from '../../types/CommonProps'

import styles from './AddToBagButton.module.scss'
import text from 'fukku/styles/texts.module.scss'
import mq from 'utils/matchMedia/styles/MatchMediaQueries.module.scss'

export type AddToBagProps = Pick<CommonProps, 'product'> & {
	selectedLookId: string
	dataTestId?: string
	onError?: (error?: string) => void // TODO: remove error optional
}

export function AddToBagButton({
	product,
	selectedLookId,
	onError,
	dataTestId,
}: AddToBagProps): React.ReactNode {
	const { t } = useLabels()
	const [isLargeOrGreater] = useMatchMedia([mq.islargeorgreater])
	const { selectedSize, setHasErrorMessage, openSheet, hasErrorMessage } =
		useSizes()
	const { selectedColor } = useColors()

	const { add } = useCartController()

	const onAdd = (): void => {
		if (selectedSize) {
			add({
				productId: product.id,
				colorId: selectedColor.id,
				sizeId: selectedSize.id,
				seller: product.seller,
				lookId: selectedLookId,
			})
		} else if (!isLargeOrGreater) {
			openSheet(true)
		} else {
			if (onError) {
				onError()
			}
			setHasErrorMessage(true)
		}
	}

	useEffect(() => {
		if (hasErrorMessage) {
			setHasErrorMessage(false)
		}
	}, [selectedColor.id])

	return (
		<>
			<div className={cx(styles.errorMessage)} aria-live='polite'>
				{hasErrorMessage && !onError && (
					<p className={cx(text.bodySRegular)}>
						{t('pdp.sizeSelector.errormessage')}
					</p>
				)}
			</div>
			<Button onClick={onAdd} data-testid={dataTestId}>
				{t('pdp.addToBag.button')}
			</Button>
		</>
	)
}
